import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
// Shared site navigation used on every page.
// Internal links use react-router Link so they stay in the same tab
// and do not full-reload. Music links to the featured releases
// anchor on the Home page (id="music").
// ─────────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { label: 'Home', to: '/' },
  { label: 'Live', to: '/live' },
  { label: 'Music', to: '/#music', hashScroll: true },
  { label: 'Book', to: '/booking' },
  { label: 'EPK', to: '/press-kit' },
];

const linkBaseCls =
  'font-body text-[11px] sm:text-[12px] tracking-[0.28em] uppercase font-medium transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[color:var(--gold)] rounded-sm';

const inactiveCls = 'text-[color:var(--ivory-muted)]/85 hover:text-[color:var(--gold)]';
const activeCls = 'text-[color:var(--gold)]';

const Nav = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  // If the URL contains a hash, scroll to it after navigation completes
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        // Small timeout so React renders the target section first
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
      }
    }
  }, [location.pathname, location.hash]);

  const renderLink = (item, extraCls = '') => {
    const isActive =
      item.to === '/'
        ? location.pathname === '/' && !location.hash
        : item.hashScroll
        ? location.hash === '#music' && location.pathname === '/'
        : location.pathname.startsWith(item.to);

    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === '/'}
        className={`${linkBaseCls} ${isActive ? activeCls : inactiveCls} ${extraCls}`}
        data-testid={`nav-link-${item.label.toLowerCase()}`}
      >
        {item.label}
      </NavLink>
    );
  };

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-[color:var(--ink)]/85 backdrop-blur-xl border-b border-[color:var(--border-soft)]"
      data-testid="site-nav"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-3">
        {/* Wordmark */}
        <Link
          to="/"
          className="font-display text-[color:var(--ivory)] hover:text-[color:var(--gold)] transition-colors leading-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[color:var(--gold)] rounded-sm"
          aria-label="Dionlovelle — Home"
          data-testid="nav-brand"
        >
          <span className="block text-[15px] sm:text-lg font-semibold tracking-[0.3em] uppercase">
            Dionlovelle
          </span>
        </Link>

        {/* Desktop nav */}
        <ul className="hidden md:flex items-center gap-7 lg:gap-9" data-testid="nav-desktop-list">
          {NAV_ITEMS.map((i) => (
            <li key={i.to}>{renderLink(i)}</li>
          ))}
        </ul>

        {/* Mobile trigger */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[color:var(--border-soft)] text-[color:var(--ivory)] hover:text-[color:var(--gold)] hover:border-[color:var(--gold)] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--gold)]"
          aria-expanded={open}
          aria-controls="mobile-nav"
          aria-label={open ? 'Close menu' : 'Open menu'}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        id="mobile-nav"
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out bg-[color:var(--ink)]/95 backdrop-blur-xl border-t border-[color:var(--border-soft)] ${
          open ? 'max-h-[420px] opacity-100' : 'max-h-0 opacity-0'
        }`}
        data-testid="nav-mobile-drawer"
      >
        <ul className="px-4 py-3 flex flex-col divide-y divide-[color:var(--border-soft)]">
          {NAV_ITEMS.map((i) => (
            <li key={i.to} className="py-3">
              {renderLink(i, 'block w-full text-left text-[13px] tracking-[0.32em]')}
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default Nav;
